// Journey-Stage Adaptive Dashboards — Broker Home (stage-adaptive).
//
// Consumes GET /api/portal/broker/dashboard (BrokerPortalDashboardDto: status,
// isCertified, server-classified `stage`, onboarding/applicant context) as the
// PRIMARY fetch that gates the page. Pre-certified brokers see a journey spine
// + onboarding/application progress + the next concrete step; certified
// brokers additionally get a deal-pipeline mini-view and a commissions widget,
// each an INDEPENDENT `useWidgetData` fetch rendered in its own `WidgetFrame`
// so one failing call degrades only its own card (audit F-11 — the old
// all-or-nothing Promise.all left the commissions card in a permanent
// skeleton and threw away a successful deals response).
//
// Stage is classified SERVER-side (PortalJourneyStages.Broker — audit
// F-24/F-25); the FE only narrows the string with `asBrokerStage` and falls
// back to "onboarding" (safe guidance) for unknown codes, never "inactive".
//
// Portal rules: no React Query — the engine's useWidgetData is a plain
// useEffect + useState fetch with a cancel guard. Only /api/portal/* calls.

import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Circle } from "lucide-react";
import { Button, buttonVariants, Skeleton } from "@/design-system/components/ui";
import { formatDateInTz, formatCurrency } from "@/design-system/lib/format";
import {
  DashboardGrid,
  JourneySpine,
  NextBestActionCard,
  ProgressBar,
  StatTile,
  StatTileRow,
} from "../dashboard/DashboardPrimitives";
import {
  PortalHomeLayout,
  useWidgetData,
  WidgetFrame,
  type WidgetData,
} from "../dashboard/engine";
import { BROKER_SPINE, asBrokerStage, brokerSpine, type BrokerStage } from "../dashboard/journey";
import { brokerApi } from "../api/broker";
import { dealSubmissionApi } from "../api/dealSubmission";
import { portalBrokerFinanceApi, type BrokerFinanceSummary } from "../api/brokerFinance";
import { chipGroupForRow } from "../pages/BrokerDealsPage";
import type {
  BrokerApplicantChecklistItemDto,
  BrokerContractPackageDto,
  BrokerDashboardChipGroup,
  BrokerPortalDashboardDto,
  CollectionPackageStatus,
  PortalDealSubmissionListDto,
  PortalDealSubmissionListItemDto,
} from "../api/types";

// F-29 — the Home mini-view loads a single page. When more submissions exist
// past this cap the tiles render "N+" (at least N) plus an explicit caption —
// never a silently wrong exact count.
export const PIPELINE_PAGE_SIZE = 200;

function isChecklistItemDone(item: BrokerApplicantChecklistItemDto): boolean {
  return item.status === "Approved" || item.status === "Waived";
}

function dealName(row: PortalDealSubmissionListItemDto): string {
  return row.businessName || row.title?.trim() || "Untitled submission";
}

// Bucket the loaded pipeline into the same chip groups the Deals tab uses
// (the server-mirrored `chipGroupForRow`), collapsed to the counts the Home
// mini-view surfaces (F-28c adds the closed/declined-or-withdrawn groups).
function pipelineCounts(items: PortalDealSubmissionListItemDto[]) {
  const g: Record<BrokerDashboardChipGroup, number> = {
    All: items.length,
    Drafts: 0,
    Submitted: 0,
    InStaging: 0,
    Returned: 0,
    Active: 0,
    Closed: 0,
    DeclinedOrWithdrawn: 0,
  };
  for (const it of items) {
    const k = chipGroupForRow(it);
    if (k) g[k] += 1;
  }
  return {
    drafts: g.Drafts,
    inReview: g.Submitted + g.InStaging,
    needsAttention: g.Returned,
    active: g.Active,
    closed: g.Closed,
    declinedOrWithdrawn: g.DeclinedOrWithdrawn,
  };
}

const PACKAGE_STATUS_LABEL: Record<CollectionPackageStatus, string> = {
  Draft: "Being prepared",
  Sent: "Waiting on you",
  InProgress: "In progress",
  Submitted: "Under review",
  PartiallyApproved: "Partially approved",
  Completed: "Complete",
  Cancelled: "Cancelled",
};

export function BrokerHomeTab() {
  const [, setParams] = useSearchParams();

  // Primary fetch — status / stage / onboarding / applicant context. The only
  // fetch allowed to gate the whole page (PortalHomeLayout contract).
  const primary = useWidgetData<BrokerPortalDashboardDto>(async () => brokerApi.dashboard(), []);
  const dash = primary.data;

  // F-24 — the server (PortalJourneyStages.Broker.Classify) is the single
  // stage source; unknown codes fall back to safe onboarding guidance.
  const stage: BrokerStage = asBrokerStage(dash?.stage) ?? "onboarding";
  const certified = dash != null && stage === "active";

  // Certified-only extras (the deal + finance endpoints 403 an uncertified
  // broker, so they're never called until the server-classified stage says
  // active). Each is its own widget fetch — independent failure (F-11).
  const pipeline = useWidgetData<PortalDealSubmissionListDto | null>(
    async () =>
      certified
        ? dealSubmissionApi.dashboard({ group: "All", pageSize: PIPELINE_PAGE_SIZE })
        : null,
    [certified],
  );
  const finance = useWidgetData<BrokerFinanceSummary | null>(
    async () => (certified ? portalBrokerFinanceApi.summary() : null),
    [certified],
  );

  const goToTab = useCallback(
    (tab: string) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set("tab", tab);
          return updated;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const stalledCount = useMemo(
    () => (pipeline.data?.items ?? []).filter((r) => r.openReturnCommentCount > 0).length,
    [pipeline.data],
  );

  const spine = brokerSpine(stage);

  return (
    <PortalHomeLayout
      loading={dash == null && primary.status === "loading"}
      error={dash == null ? primary.error : null}
      onRetry={primary.reload}
      stage={stage}
      testId="broker-home"
    >
      {dash != null && (
        <>
          {/* F-26 — an inactive account renders NO journey spine: the account
              being shut off is not a journey position; the hero stands alone. */}
          {stage !== "inactive" && (
            <JourneySpine
              steps={BROKER_SPINE}
              currentIndex={spine.index}
              complete={spine.complete}
              testId="broker-home-spine"
            />
          )}

          <BrokerNextStep
            stage={stage}
            dash={dash}
            stalledCount={stalledCount}
            onGoToTab={goToTab}
          />

          {stage === "onboarding" && (
            <DashboardGrid>
              <OnboardingProgressCard dash={dash} />
            </DashboardGrid>
          )}

          {stage === "active" && (
            <DashboardGrid>
              <PipelineWidget state={pipeline} onGoToTab={goToTab} />
              <CommissionsWidget state={finance} onGoToTab={goToTab} />
            </DashboardGrid>
          )}
        </>
      )}
    </PortalHomeLayout>
  );
}

// ── Next best action ─────────────────────────────────────────────────────────

function BrokerNextStep({
  stage,
  dash,
  stalledCount,
  onGoToTab,
}: {
  stage: BrokerStage;
  dash: BrokerPortalDashboardDto;
  stalledCount: number;
  onGoToTab: (tab: string) => void;
}) {
  if (stage === "inactive") {
    return (
      <NextBestActionCard
        heading="Your broker account isn't active"
        description="This account isn't currently active for deal submission. Reach out to your Phoenix broker contact if you think this is a mistake."
        testId="broker-home-next"
      />
    );
  }

  if (stage === "onboarding") {
    const applicant = dash.applicant;
    const openItems = applicant?.checklist.filter((i) => !isChecklistItemDone(i)).length ?? 0;
    const op = dash.onboardingProgress;
    const courseActionable = op != null && !op.courseComplete && op.totalItems > 0;

    // F-28a — a returned application outranks everything else: the reviewer
    // explicitly asked for changes, so surface their note + when.
    if (applicant?.applicationStatus === "ChangesRequested") {
      const parts = [
        applicant.returnedAt
          ? `Returned ${formatDateInTz(applicant.returnedAt)}.`
          : null,
        applicant.reviewerNote ? `Reviewer note: “${applicant.reviewerNote}”` : null,
        "Update your answers and resubmit to keep your application moving.",
      ].filter(Boolean);
      return (
        <NextBestActionCard
          heading="Your application was returned"
          description={parts.join(" ")}
          tone="warning"
          actions={
            <Button
              variant="primary"
              size="sm"
              onClick={() => onGoToTab("requests")}
              data-testid="broker-home-open-requests"
            >
              Go to your requests
            </Button>
          }
          testId="broker-home-next"
        />
      );
    }

    const applicationCard =
      openItems > 0 ? (
        <NextBestActionCard
          heading="Complete your application"
          description={`${openItems} item${openItems === 1 ? "" : "s"} still ${
            openItems === 1 ? "needs" : "need"
          } your attention before we can qualify you.`}
          actions={
            <Button
              variant="primary"
              size="sm"
              onClick={() => onGoToTab("requests")}
              data-testid="broker-home-open-requests"
            >
              Go to your requests
            </Button>
          }
          testId="broker-home-next"
        />
      ) : null;

    // F-27 — a Prospect advances via qualification (the application), so an
    // in-flight application outranks the onboarding course; the course leads
    // for Qualified / Onboarding brokers, matching the certification ladder.
    if (dash.status === "Prospect" && applicationCard) return applicationCard;

    if (courseActionable) {
      return (
        <NextBestActionCard
          heading="Finish your onboarding course"
          description={`${op.completedItems} of ${op.totalItems} steps complete — complete every item to get certified.`}
          actions={
            <Link
              to="/portal/broker/course"
              className={`${buttonVariants({ variant: "primary", size: "sm" })} no-underline`}
              data-testid="broker-home-continue-course"
            >
              {op.completedItems > 0 ? "Continue onboarding" : "Start onboarding"}
            </Link>
          }
          testId="broker-home-next"
        />
      );
    }

    if (applicationCard) return applicationCard;

    return (
      <NextBestActionCard
        heading="We're getting you set up"
        description="Your Phoenix broker team is reviewing your details. We'll let you know as soon as there's a next step."
        testId="broker-home-next"
      />
    );
  }

  // stage === "active" (certified). A returned/commented deal outranks the
  // steady-state "submit a new deal" nudge.
  if (stalledCount > 0) {
    return (
      <NextBestActionCard
        heading={`${stalledCount} deal${stalledCount === 1 ? "" : "s"} ${
          stalledCount === 1 ? "needs" : "need"
        } your attention`}
        description="A liaison left comments on deals you've submitted. Review and respond to keep them moving."
        tone="warning"
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => onGoToTab("deals")}
            data-testid="broker-home-review-deals"
          >
            Review deals
          </Button>
        }
        testId="broker-home-next"
      />
    );
  }

  return (
    <NextBestActionCard
      heading="Submit a new deal"
      description="You're certified — submit a new deal or track where your pipeline stands."
      actions={
        <>
          <Link
            to="/portal/broker/deals/new"
            className={`${buttonVariants({ variant: "primary", size: "sm" })} no-underline`}
            data-testid="broker-home-submit-deal"
          >
            Submit a new deal
          </Link>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onGoToTab("deals")}
            data-testid="broker-home-view-deals"
          >
            View your deals
          </Button>
        </>
      }
      testId="broker-home-next"
    />
  );
}

// ── Onboarding progress (pre-certified) ──────────────────────────────────────

function contractPackageLine(pkg: BrokerContractPackageDto, index: number, total: number): string {
  const noun = `Contract package${total > 1 ? ` ${index + 1}` : ""}`;
  const statusLabel = PACKAGE_STATUS_LABEL[pkg.status] ?? pkg.status;
  return `${noun}: ${pkg.completedItems} of ${pkg.totalItems} item${
    pkg.totalItems === 1 ? "" : "s"
  } complete · ${statusLabel}`;
}

function OnboardingProgressCard({ dash }: { dash: BrokerPortalDashboardDto }) {
  const op = dash.onboardingProgress;
  const checklist = dash.applicant?.checklist ?? [];

  // Prefer onboarding-course progress; fall back to application-checklist
  // completion when the course hasn't started yet.
  const total = op?.totalItems ?? checklist.length;
  const done = op?.completedItems ?? checklist.filter(isChecklistItemDone).length;
  // F-28b — per-package status instead of one blended aggregate line.
  const packages = dash.contractPackages.filter((p) => p.totalItems > 0);

  return (
    <WidgetFrame title="Getting certified" span="full" testId="broker-home-onboarding">
      <div className="space-y-space-3">
        {/* F-40 — the shared portal ProgressBar, never a hand-rolled bar. */}
        <ProgressBar
          fraction={total > 0 ? done / total : 0}
          label={total > 0 ? `${done} of ${total} complete` : "Getting started"}
          testId="broker-home-onboarding-progress"
        />

        {checklist.length > 0 && (
          <ul
            className="flex flex-col gap-space-2 list-none p-0 m-0"
            data-testid="broker-home-checklist"
          >
            {checklist.map((item) => {
              const itemDone = isChecklistItemDone(item);
              return (
                <li key={item.itemId} className="flex items-center gap-space-2 text-small">
                  {itemDone ? (
                    <CheckCircle2
                      size={16}
                      strokeWidth={1.5}
                      className="text-status-success shrink-0"
                      aria-hidden
                    />
                  ) : (
                    <Circle
                      size={16}
                      strokeWidth={1.5}
                      className="text-gray-mid shrink-0"
                      aria-hidden
                    />
                  )}
                  <span className={itemDone ? "text-gray-mid line-through" : "text-onyx"}>
                    {item.title}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {packages.length > 0 && (
          <div className="space-y-space-1" data-testid="broker-home-contract">
            {packages.map((pkg, i) => (
              <p key={pkg.packageId} className="text-caption text-gray-mid m-0">
                {contractPackageLine(pkg, i, packages.length)}
              </p>
            ))}
          </div>
        )}
      </div>
    </WidgetFrame>
  );
}

// ── Pipeline mini-view (certified) ───────────────────────────────────────────

function PipelineWidget({
  state,
  onGoToTab,
}: {
  state: WidgetData<PortalDealSubmissionListDto | null>;
  onGoToTab: (tab: string) => void;
}) {
  const list = state.data;
  const items = useMemo(() => list?.items ?? [], [list]);
  const counts = useMemo(() => pipelineCounts(items), [items]);
  const stalled = useMemo(() => items.filter((r) => r.openReturnCommentCount > 0), [items]);

  // F-29 — the server reports the true total; when it exceeds the loaded
  // page the per-group tiles are lower bounds, shown as "N+".
  const truncated = list != null && list.totalCount > list.items.length;
  const tileValue = (n: number) => (truncated ? `${n}+` : n);

  return (
    <WidgetFrame
      title="Your pipeline"
      span="full"
      state={state}
      testId="broker-home-pipeline"
      action={
        <Button
          variant="text"
          size="sm"
          onClick={() => onGoToTab("deals")}
          data-testid="broker-home-pipeline-all"
        >
          View all
        </Button>
      }
    >
      {list == null ? (
        <Skeleton className="h-24 w-full" />
      ) : items.length === 0 ? (
        <p className="text-small text-gray-mid m-0" data-testid="broker-home-pipeline-empty">
          You haven&apos;t submitted any deals yet.
        </p>
      ) : (
        <div className="space-y-space-3">
          <StatTileRow>
            <StatTile
              label="Drafts"
              value={tileValue(counts.drafts)}
              testId="broker-home-stat-drafts"
            />
            <StatTile
              label="In review"
              value={tileValue(counts.inReview)}
              testId="broker-home-stat-in-review"
            />
            <StatTile
              label="Needs attention"
              value={tileValue(counts.needsAttention)}
              tone={counts.needsAttention > 0 ? "warning" : "default"}
              testId="broker-home-stat-attention"
            />
            <StatTile
              label="Active"
              value={tileValue(counts.active)}
              testId="broker-home-stat-active"
            />
          </StatTileRow>

          {/* F-28c — the computed-but-untiled terminal groups as a muted
              secondary line (no extra tiles — they're history, not work). */}
          {(counts.closed > 0 || counts.declinedOrWithdrawn > 0) && (
            <p
              className="text-caption text-gray-mid m-0"
              data-testid="broker-home-pipeline-terminal"
            >
              {[
                counts.closed > 0 ? `${tileValue(counts.closed)} closed` : null,
                counts.declinedOrWithdrawn > 0
                  ? `${tileValue(counts.declinedOrWithdrawn)} declined or withdrawn`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}

          {truncated && (
            <p
              className="text-caption text-gray-mid m-0"
              data-testid="broker-home-pipeline-truncated"
            >
              Counts reflect your {items.length} most recent submissions of {list.totalCount} —
              see the Deals tab for the full pipeline.
            </p>
          )}

          {stalled.length > 0 && (
            <div className="space-y-space-2" data-testid="broker-home-stalled">
              <p className="text-caption uppercase tracking-wide text-gray-mid m-0">
                Needs your attention
              </p>
              <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
                {stalled.slice(0, 4).map((row) => (
                  <li key={row.id} className="flex items-center gap-space-2 text-small">
                    <AlertTriangle
                      size={14}
                      className="text-status-warning shrink-0"
                      aria-hidden
                    />
                    <Link
                      to={`/portal/broker/deals/submissions/${row.id}/status`}
                      className="text-onyx hover:underline truncate"
                      data-testid={`broker-home-stalled-${row.id}`}
                    >
                      {dealName(row)}
                    </Link>
                    <span className="text-caption text-status-warning shrink-0">
                      {row.openReturnCommentCount} comment
                      {row.openReturnCommentCount === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </WidgetFrame>
  );
}

// ── Commissions (certified) ──────────────────────────────────────────────────

function CommissionsWidget({
  state,
  onGoToTab,
}: {
  state: WidgetData<BrokerFinanceSummary | null>;
  onGoToTab: (tab: string) => void;
}) {
  const summary = state.data;
  return (
    <WidgetFrame
      title="Commissions"
      span="full"
      state={state}
      testId="broker-home-commissions"
      action={
        <Button
          variant="text"
          size="sm"
          onClick={() => onGoToTab("payments")}
          data-testid="broker-home-commissions-all"
        >
          View details
        </Button>
      }
    >
      {summary == null ? (
        <Skeleton className="h-24 w-full" />
      ) : summary.paymentCount === 0 ? (
        <p className="text-small text-gray-mid m-0" data-testid="broker-home-commissions-empty">
          Your commission summary will appear here once a loan you brokered starts paying.
        </p>
      ) : (
        <div className="space-y-space-3">
          <StatTileRow>
            <StatTile
              label="Total earned"
              value={formatCurrency(summary.totalEarned)}
              hint="Lifetime, paid to date"
              testId="broker-home-commission-total"
            />
            {/* F-12 — real recognized year-to-date, server-computed. */}
            <StatTile
              label="Paid this year"
              value={
                summary.paidYearToDate != null ? formatCurrency(summary.paidYearToDate) : "—"
              }
              hint={`${new Date().getFullYear()} to date`}
              testId="broker-home-commission-ytd"
            />
            <StatTile
              label="Loans paid"
              value={summary.loanCount}
              testId="broker-home-commission-loans"
            />
            <StatTile
              label="Last paid"
              value={summary.lastPaidAt ? formatDateInTz(summary.lastPaidAt) : "—"}
              hint={`${summary.paymentCount} payment${summary.paymentCount === 1 ? "" : "s"}`}
              testId="broker-home-commission-last"
            />
          </StatTileRow>
          <p className="text-caption text-gray-mid m-0">
            Recognized commission on the loans you brokered.
          </p>
        </div>
      )}
    </WidgetFrame>
  );
}
